import { memo as Memo, useMemo, useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';

//* HOC's
import { withDataContext } from 'context';

//* Helpers
import { routerToPathAndQuery } from 'helpers/_routerToPathAndQuery';

//* Components
import Page from 'components/common/Page';
import Container from 'components/common/Container';
import Text from 'components/common/Text';
import Empty from 'components/common/Empty';
import Filter from 'components/pages/media/Filter';
import List from 'components/pages/media/List';
import LottieAnimation from 'components/common/LottieAnimation';

//* Styles
import MediaLibraryStyle from 'styles/pages/MediaLibraryStyle';

//* Json
import loadingLottie from 'lottieFiles/loading.json';

const MediaLibrary = Memo(({ pages, medias, pageParams, selectedLang, loading }) => {
    //! Router
    const router = useRouter();

    //! Query Param Keys
    const queryParamKeys = useMemo(() => pageParams.postType.queryParamKeys, [pageParams]);

    //! Page Data
    const pageData = useMemo(() => pages?.[pageParams.name]?.[selectedLang], [pages, pageParams, selectedLang]);
    const mediaData = useMemo(() => medias?.[pageParams?.queryName]?.[selectedLang], [medias, selectedLang, pageParams.queryName]);

    //! On Category Change
    const onCategoryChange = useCallback(
        (type, typeId) => {
            const { pathname, query } = routerToPathAndQuery(router, type, typeId, false);
            type != pageParams.postType.queryParamKeys[3] && delete query[pageParams.postType.queryParamKeys[3]]; //? Page Param Removed on Category Change
            router.push({ pathname, query }, undefined, { scroll: false });
        },
        [router.query]
    );

    //! Scroll to top
    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [loading]);

    return (
        <Page
            meta={pageData?.content?.meta}
            nextSeoProps={{
                noindex: true,
                nofollow: true
            }}
            hrefLang={false}
            transparentHeader={pageParams.transparentHeader && !pageData}
        >
            {pageData && (
                <MediaLibraryStyle>
                    <Container
                        row
                        section>
                        <Text
                            tag={'h3'}
                            className={'media-library-title font-ping-bolder'}
                            text={pageData.content.title}
                        />
                        <Filter
                            categories={pageData.filters.categories}
                            types={pageData.filters.types}
                            orders={pageData.filters.orders}
                            onCategoryChange={onCategoryChange}
                            router={router}
                            queryParamKeys={queryParamKeys}
                        />
                        <div className='col-9 col-ts-12 col-m-12'>
                            {!loading && mediaData ? (
                                mediaData.total > 0 ? (
                                    <List
                                        data={mediaData}
                                        isAll={!router.query?.type}
                                        onCategoryChange={onCategoryChange}
                                    />
                                ) : (
                                    <Empty />
                                )
                            ) : (
                                <div className='lottie-box'>
                                    <LottieAnimation
                                        animData={loadingLottie}
                                        autoplay={true}
                                        isPlay={true}
                                        loop={true}
                                    />
                                </div>
                            )}
                        </div>
                    </Container>
                </MediaLibraryStyle>
            )}
        </Page>
    );
});

export default withDataContext(MediaLibrary, ['pages', 'medias', 'loading']);
